import { Editor } from '@tiptap/core';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionNotation,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
} from '@bonusly/common-action-input';
import { Box, Flex, Hide, HStack, ModalFooter, rem, Show, useBreakpointValue } from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { Meter } from '../../../gratitude-gauge/meter';
import { ScoreBoxAccordion } from '../../../gratitude-gauge/scorebox-accordion';
import { ScoreBoxPopout } from '../../../gratitude-gauge/scorebox-popout';
import { useGratitudeGauge } from '../../../gratitude-gauge/use-gratitude-gauge';
import { useGiveBox } from '../../hooks/use-give-box';
import { useInputFocus } from '../../hooks/use-input-focus';
import { GiveModalContentBody } from './give-modal-content-body';

interface GiveModalContentProps {
	content?: any;
	onSuccess: VoidFunction;
	setIsSubmitting: (isSubmitting: boolean) => void;
	onVideoAttachmentChange?: (hasVideo: boolean) => void;
	via?: string;
}

export interface GiveModalContentHandle {
	hasVideoAttachment: () => boolean;
}

const GRATITUDE_GAUGE_MOBILE_CUTOFF = 1440;

export const GiveModalContent = forwardRef<GiveModalContentHandle, GiveModalContentProps>(
	({ content, onSuccess, setIsSubmitting, onVideoAttachmentChange, via }, ref) => {
		const config = useGiveBox({ onSuccess, via });
		const { isFocused, ...handlers } = useInputFocus();
		const [videoAttachment, setVideoAttachment] = useState<boolean>(false);
		const gratitudeGauge = useGratitudeGauge();
		const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

		// this hook allows us to expose data to parent compoenents. Specifically, they need to know
		// if there is a video attachment so they can confirm the user wants to delete it before closing
		// the modal.
		useImperativeHandle(ref, () => ({
			hasVideoAttachment: () => videoAttachment,
		}));

		useEffect(() => {
			setIsSubmitting(config.isSubmitting);
		}, [config.isSubmitting, setIsSubmitting]);

		// The `checkVideoAttachment` function is needed to maintain the state of the video attachment within the GiveModalContent component.
		// It serves the following purposes:
		// 1. State Synchronization: It ensures that the `videoAttachment` state is always in sync with the actual data present in the ActionInput component.
		//    This is important because the presence of a video attachment affects the behavior of the modal, particularly when the user attempts to close it.
		// 2. User Confirmation: By detecting changes in the video attachment state, it allows the parent component to prompt the user for confirmation
		//    before closing the modal if a video is attached. This prevents accidental loss of data, which is especially important for video files that
		//    might take significant time and resources to upload.
		// 3. Real-time Updates: The function is called immediately and at regular intervals to check for changes in the attachment state.
		//    This real-time checking is necessary because the attachment state can change due to user actions or asynchronous operations.
		// 4. Callback Invocation: It invokes the `onVideoAttachmentChange` callback whenever there is a change in the video attachment state.
		//    This allows other components or hooks to react to changes, enabling a more dynamic and responsive UI.
		const checkVideoAttachment = useCallback(() => {
			const data = config.ref.current?.data();
			const hasVideo = data?.attachment?.category === 'video';

			// Ughh I don't like doing this at all. We really need to fix the state management with the give box
			if (videoAttachment && !hasVideo) {
				gratitudeGauge.onChangeTranscription('');
			}

			if (hasVideo !== videoAttachment) {
				setVideoAttachment(hasVideo);
				onVideoAttachmentChange?.(hasVideo);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- gratitudeGauge returns unstable object reference, intentionally omitted
		}, [config.ref, onVideoAttachmentChange, videoAttachment]);

		useEffect(() => {
			// Check immediately
			checkVideoAttachment();
			// Set up an interval to check for changes. We're operation outside
			// the ActionMedia provider and don't have access to media state.
			const interval = setInterval(checkVideoAttachment, 100);
			// Cleanup the interval when the component unmounts
			return () => clearInterval(interval);
		}, [checkVideoAttachment]);

		const isMobile = useBreakpointValue({ base: true, sm: false });

		const handleUpdate = ({ editor }: { editor: Editor }) => {
			gratitudeGauge.onChangeText(editor.getText().trim());
		};

		// ============================================
		// REDESIGNED LAYOUT (homepage-redesign is enabled)
		// ============================================
		if (isHomepageRedesignEnabled) {
			return (
				<ActionInput autofocus="end" content={content} onUpdate={handleUpdate} {...config}>
					<GiveModalContentBody display="flex" flexDirection="column" flex="1" px={{ base: 0, sm: 6 }} pt={{ base: 4, sm: 0 }} pb={0} {...handlers}>
						<Show breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
							<ScoreBoxPopout dimensions={gratitudeGauge.dimensions} isLoading={gratitudeGauge.isFetching} text={gratitudeGauge.text} />
						</Show>

						{/* Visibility dropdown at the top - same location for mobile and desktop */}
						<Box px={{ base: 4, sm: 0 }}>
							<ActionPrivacy />
						</Box>

						{/* Text input area */}
						<Box flex="1" position="relative" display="flex" flexDirection="column" px={{ base: 4, sm: 0 }}>
							<ActionText
								height="100%"
								inputStyles={
									isMobile
										? {
												flex: '1',
												minHeight: '0',
												display: 'flex',
												flexDirection: 'column',
												position: 'relative',
												padding: '0.5rem 0',
											}
										: { minHeight: rem(120), padding: '0.5rem 0' }
								}
								display="flex"
								flexDirection="column"
								flex="1"
								minHeight={0}
								overflow="hidden"
							/>
							<ActionFeedback mb={2} />
							<ActionMedia w="full" />
						</Box>
					</GiveModalContentBody>

					<ModalFooter display="block" px={{ base: 0, sm: 6 }} py={4} overflow="hidden" borderBottomRadius="10px">
						{/* Desktop: Bottom toolbar - Tags on left, Action buttons on right */}
						<HStack justifyContent="space-between" mb={2} gap={2} px={{ base: 4, md: 0 }}>
							<ActionTags />
							<ActionDecorations />
						</HStack>

						{/* Full width submit button */}
						<Box px={{ base: 4, sm: 0 }}>
							<ActionSubmit size="lg" w="full" />
						</Box>

						<Hide breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
							<Box px={{ base: 4, sm: 0 }} pt={2} pb={2}>
								<Box borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="border.default">
									<ScoreBoxAccordion dimensions={gratitudeGauge.dimensions} isLoading={gratitudeGauge.isFetching} text={gratitudeGauge.text} />
								</Box>
							</Box>
						</Hide>

						<Show breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
							<Box mx={-6} mb={-4} mt={2}>
								<Meter matches={videoAttachment ? gratitudeGauge.totalDimensions : gratitudeGauge.totalMatches} total={gratitudeGauge.totalDimensions} />
							</Box>
						</Show>
					</ModalFooter>
				</ActionInput>
			);
		}

		// ============================================
		// ORIGINAL LAYOUT (homepage-redesign is not enabled)
		// ============================================
		return (
			<ActionInput autofocus="end" content={content} onUpdate={handleUpdate} {...config}>
				<GiveModalContentBody display="flex" flexDirection="column" flex="1" pt={4} {...handlers}>
					<Show breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
						<ScoreBoxPopout dimensions={gratitudeGauge.dimensions} isLoading={gratitudeGauge.isFetching} text={gratitudeGauge.text} />
					</Show>

					<Flex alignItems="flex-start" justifyContent="space-between">
						<ActionTags />
						<Hide below="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<Box flex="1" position="relative" display="flex" flexDirection="column">
						<ActionText
							height="100%"
							inputStyles={
								isMobile
									? {
											flex: '1',
											minHeight: '0',
											display: 'flex',
											flexDirection: 'column',
											position: 'relative',
										}
									: { minHeight: rem(150) }
							}
							display="flex"
							flexDirection="column"
							flex="1"
							minHeight={0}
							overflow="hidden"
						/>
						<ActionNotation isVisible={isFocused} />
						<ActionFeedback mb={4} />
						<ActionMedia w="full" />
					</Box>
				</GiveModalContentBody>

				<ModalFooter display="block" px={0} py={0} overflow="hidden" borderBottomRadius="10px">
					<Flex
						alignItems={{ base: 'stretch', xs: 'flex-start' }}
						direction={{ base: 'column', xs: 'row' }}
						gap={2}
						justifyContent="space-between"
						mb={2}
						w="full"
						px={6}
						pt={4}
						pb={3}
					>
						<Flex justifyContent="space-between" flexGrow={1} alignItems="center">
							<ActionDecorations />
							<Hide above="xs">
								<ActionPrivacy position="fixed" zIndex={2} top={rem(22)} right={{ base: rem(50), '2xs': rem(20) }} border="none" />
							</Hide>
							<ActionSubmit size="lg" />
						</Flex>
					</Flex>

					<Hide breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
						<ScoreBoxAccordion dimensions={gratitudeGauge.dimensions} isLoading={gratitudeGauge.isFetching} text={gratitudeGauge.text} />
					</Hide>

					<Show breakpoint={`(min-width: ${GRATITUDE_GAUGE_MOBILE_CUTOFF}px)`}>
						<Meter matches={videoAttachment ? gratitudeGauge.totalDimensions : gratitudeGauge.totalMatches} total={gratitudeGauge.totalDimensions} />
					</Show>
				</ModalFooter>
			</ActionInput>
		);
	}
);

GiveModalContent.displayName = 'GiveModalContent';
